// src/admin/pages/NewVehicle.jsx
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { Card } from "../ui/Card";
import { Badge } from "../ui/Badge";

/**
 * ✅ Matches your CURRENT DB schema exactly (tables + column names)
 * Creates:
 *  1) public.vehicles (core)
 *  2) category details row (vehicle_atv / vehicle_boat / vehicle_sxs / etc) using vehicle_id
 *
 * Notes:
 * - vehicles.category must be one of:
 *   travel_trailer | boat | atv | sxs | snowmobile | golf_cart | watercraft
 * - Use odometer_km (NOT km) everywhere it exists in detail tables
 * - Use weight_lb / gvwr_lb columns exactly as shown in your schema
 */

const CATEGORY_OPTIONS = [
  { label: "Travel Trailer", value: "travel_trailer" },
  { label: "ATV", value: "atv" },
  { label: "Side-by-Side (SxS)", value: "sxs" },
  { label: "Boat", value: "boat" },
  { label: "Watercraft (PWC)", value: "watercraft" },
  { label: "Snowmobile", value: "snowmobile" },
  { label: "Golf Cart", value: "golf_cart" },
];

const STATUS_OPTIONS = [
  "available",
  "booked",
  "maintenance",
  "cleaning",
  "transit",
  "ops_hold",
];

const TIER_OPTIONS = ["Explore", "Adventure", "Elite", "Platinum"];

const BOAT_STEERING = [
  { label: "Console", value: "console" },
  { label: "Tiller", value: "tiller" },
];

const GC_ENGINE_TYPE = [
  { label: "Gas", value: "gas" },
  { label: "Electric", value: "electric" },
];

// ---------- helpers ----------
function normalize(s) {
  return String(s || "").trim();
}
function toNull(s) {
  const v = normalize(s);
  return v ? v : null;
}
function toInt(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseInt(String(v), 10);
  return Number.isNaN(n) ? null : n;
}
function toNum(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(String(v));
  return Number.isNaN(n) ? null : n;
}
function toBool(v) {
  return !!v;
}

/**
 * ✅ Detail table/column names match your schema:
 * - vehicle_atv: odometer_km, weight_lb, vin, seats, power_steering, winch, engine_cc, drivetrain
 * - vehicle_boat: engine_hours, hin, trailer_vin, trailer_plate, boat_registration_number, weight_lb, livewell, trolling_motor, fish_finder_chartplotter, steering_type, plus existing columns
 * - vehicle_sxs: odometer_km, vin, plate, weight_lb, drivetrain, winch, dump_box, seats, engine_cc
 * - vehicle_snowmobile: odometer_km, vin, seats, power_steering, engine_type, turbo, plate, weight_lb, engine_cc, track_length_in, heated_grips
 * - vehicle_travel_trailer: vin, gvwr_lb, plate, plus existing columns
 * - vehicle_watercraft: engine_hours, hin, boat_registration_number, trailer_vin, trailer_plate, seats, horse_power, engine_cc, supercharged, stereo
 * - vehicle_golf_cart: odometer_km, vin, plate, weight_lb, engine_type, seats
 */
const DETAIL_CONFIG = {
  atv: {
    table: "vehicle_atv",
    title: "ATV details",
    fields: [
      { key: "odometer_km", label: "Odometer (KM)", type: "number" },
      { key: "vin", label: "VIN", type: "text" },
      { key: "seats", label: "Seats", type: "number" },
      { key: "weight_lb", label: "Weight (lb)", type: "number" },
      { key: "power_steering", label: "Power steering", type: "checkbox" },
      { key: "winch", label: "Winch", type: "checkbox" },
      { key: "engine_cc", label: "Engine CC (optional)", type: "number" },
      { key: "drivetrain", label: "Drivetrain (optional)", type: "text", placeholder: "AWD/4x4/2WD" },
    ],
    buildPayload: (d) => ({
      odometer_km: toInt(d.odometer_km),
      vin: toNull(d.vin),
      seats: toInt(d.seats),
      weight_lb: toInt(d.weight_lb),
      power_steering: toBool(d.power_steering),
      winch: toBool(d.winch),
      engine_cc: toInt(d.engine_cc),
      drivetrain: toNull(d.drivetrain),
    }),
  },

  boat: {
    table: "vehicle_boat",
    title: "Boat details",
    fields: [
      { key: "engine_hours", label: "Engine Hours", type: "number" },
      { key: "hin", label: "HIN", type: "text" },
      { key: "trailer_vin", label: "Trailer VIN", type: "text" },
      { key: "trailer_plate", label: "Trailer Plate", type: "text" },
      { key: "boat_registration_number", label: "Boat Registration #", type: "text" },
      { key: "weight_lb", label: "Weight (lb)", type: "number" },
      { key: "livewell", label: "Livewell", type: "checkbox" },
      { key: "trolling_motor", label: "Trolling motor", type: "checkbox" },
      { key: "fish_finder_chartplotter", label: "Fish finder & chart plotter", type: "checkbox" },
      { key: "steering_type", label: "Steering", type: "select", options: BOAT_STEERING },

      // existing columns you already had in the table (optional inputs)
      { key: "length_ft", label: "Length (ft) (optional)", type: "number" },
      { key: "engine_type", label: "Engine Type (optional)", type: "text" },
      { key: "engine_hp", label: "Engine HP (optional)", type: "number" },
      { key: "capacity", label: "Capacity (optional)", type: "number" },
      { key: "fuel_type", label: "Fuel Type (optional)", type: "text" },
      { key: "trailer_included", label: "Trailer Included (optional)", type: "checkbox" },
    ],
    buildPayload: (d) => ({
      engine_hours: toNum(d.engine_hours),
      hin: toNull(d.hin),
      trailer_vin: toNull(d.trailer_vin),
      trailer_plate: toNull(d.trailer_plate),
      boat_registration_number: toNull(d.boat_registration_number),
      weight_lb: toInt(d.weight_lb),
      livewell: toBool(d.livewell),
      trolling_motor: toBool(d.trolling_motor),
      fish_finder_chartplotter: toBool(d.fish_finder_chartplotter),
      steering_type: toNull(d.steering_type),

      length_ft: toNum(d.length_ft),
      engine_type: toNull(d.engine_type),
      engine_hp: toInt(d.engine_hp),
      capacity: toInt(d.capacity),
      fuel_type: toNull(d.fuel_type),
      trailer_included: toBool(d.trailer_included),
    }),
  },

  sxs: {
    table: "vehicle_sxs",
    title: "Side-by-Side details",
    fields: [
      { key: "odometer_km", label: "Odometer (KM)", type: "number" },
      { key: "vin", label: "VIN", type: "text" },
      { key: "plate", label: "Plate", type: "text" },
      { key: "weight_lb", label: "Weight (lb)", type: "number" },
      { key: "seats", label: "Seats", type: "number" },
      { key: "engine_cc", label: "Engine CC (optional)", type: "number" },
      { key: "drivetrain", label: "Drivetrain (optional)", type: "text" },
      { key: "winch", label: "Winch", type: "checkbox" },
      { key: "dump_box", label: "Dump box", type: "checkbox" },
    ],
    buildPayload: (d) => ({
      odometer_km: toInt(d.odometer_km),
      vin: toNull(d.vin),
      plate: toNull(d.plate),
      weight_lb: toInt(d.weight_lb),
      seats: toInt(d.seats),
      engine_cc: toInt(d.engine_cc),
      drivetrain: toNull(d.drivetrain),
      winch: toBool(d.winch),
      dump_box: toBool(d.dump_box),
    }),
  },

  snowmobile: {
    table: "vehicle_snowmobile",
    title: "Snowmobile details",
    fields: [
      { key: "odometer_km", label: "Odometer (KM)", type: "number" },
      { key: "vin", label: "VIN", type: "text" },
      { key: "seats", label: "Seats", type: "number" },
      { key: "power_steering", label: "Power steering", type: "checkbox" },
      { key: "engine_type", label: "Engine Type", type: "text" },
      { key: "turbo", label: "Turbo", type: "checkbox" },
      { key: "plate", label: "Plate", type: "text" },
      { key: "weight_lb", label: "Weight (lb)", type: "number" },

      // existing optional columns
      { key: "engine_cc", label: "Engine CC (optional)", type: "number" },
      { key: "track_length_in", label: "Track length (in) (optional)", type: "number" },
      { key: "heated_grips", label: "Heated grips (optional)", type: "checkbox" },
    ],
    buildPayload: (d) => ({
      odometer_km: toInt(d.odometer_km),
      vin: toNull(d.vin),
      seats: toInt(d.seats),
      power_steering: toBool(d.power_steering),
      engine_type: toNull(d.engine_type),
      turbo: toBool(d.turbo),
      plate: toNull(d.plate),
      weight_lb: toInt(d.weight_lb),

      engine_cc: toInt(d.engine_cc),
      track_length_in: toInt(d.track_length_in),
      heated_grips: toBool(d.heated_grips),
    }),
  },

  travel_trailer: {
    table: "vehicle_travel_trailer",
    title: "Trailer details",
    fields: [
      { key: "vin", label: "VIN", type: "text" },
      { key: "plate", label: "Plate", type: "text" },
      { key: "gvwr_lb", label: "GVWR (lb)", type: "number" },

      // existing trailer columns
      { key: "sleeps", label: "Sleeps (optional)", type: "number" },
      { key: "length_ft", label: "Length (ft) (optional)", type: "number" },
      { key: "dry_weight_lb", label: "Dry weight (lb) (optional)", type: "number" },
      { key: "hitch_weight_lb", label: "Hitch weight (lb) (optional)", type: "number" },
      { key: "slides", label: "Slides (optional)", type: "number" },
      { key: "fresh_water_gal", label: "Fresh water (gal) (optional)", type: "number" },
      { key: "grey_water_gal", label: "Grey water (gal) (optional)", type: "number" },
      { key: "black_water_gal", label: "Black water (gal) (optional)", type: "number" },
    ],
    buildPayload: (d) => ({
      vin: toNull(d.vin),
      plate: toNull(d.plate),
      gvwr_lb: toInt(d.gvwr_lb),

      sleeps: toInt(d.sleeps),
      length_ft: toNum(d.length_ft),
      dry_weight_lb: toInt(d.dry_weight_lb),
      hitch_weight_lb: toInt(d.hitch_weight_lb),
      slides: toInt(d.slides),
      fresh_water_gal: toInt(d.fresh_water_gal),
      grey_water_gal: toInt(d.grey_water_gal),
      black_water_gal: toInt(d.black_water_gal),
    }),
  },

  watercraft: {
    table: "vehicle_watercraft",
    title: "Watercraft details",
    fields: [
      { key: "engine_hours", label: "Engine Hours", type: "number" },
      { key: "hin", label: "HIN", type: "text" },
      { key: "boat_registration_number", label: "Boat Registration #", type: "text" },
      { key: "trailer_vin", label: "Trailer VIN", type: "text" },
      { key: "trailer_plate", label: "Trailer Plate", type: "text" },
      { key: "seats", label: "Seats", type: "number" },
      { key: "horse_power", label: "Horse Power", type: "number" },
      { key: "engine_cc", label: "Engine CC", type: "number" },
      { key: "supercharged", label: "Supercharged", type: "checkbox" },
      { key: "stereo", label: "Stereo", type: "checkbox" },
    ],
    buildPayload: (d) => ({
      engine_hours: toNum(d.engine_hours),
      hin: toNull(d.hin),
      boat_registration_number: toNull(d.boat_registration_number),
      trailer_vin: toNull(d.trailer_vin),
      trailer_plate: toNull(d.trailer_plate),
      seats: toInt(d.seats),
      horse_power: toInt(d.horse_power),
      engine_cc: toInt(d.engine_cc),
      supercharged: toBool(d.supercharged),
      stereo: toBool(d.stereo),
    }),
  },

  golf_cart: {
    table: "vehicle_golf_cart",
    title: "Golf cart details",
    fields: [
      { key: "odometer_km", label: "Odometer (KM)", type: "number" },
      { key: "vin", label: "VIN", type: "text" },
      { key: "plate", label: "Plate", type: "text" },
      { key: "weight_lb", label: "Weight (lb)", type: "number" },
      { key: "seats", label: "Seats", type: "number" },
      { key: "engine_type", label: "Engine Type", type: "select", options: GC_ENGINE_TYPE },
    ],
    buildPayload: (d) => ({
      odometer_km: toInt(d.odometer_km),
      vin: toNull(d.vin),
      plate: toNull(d.plate),
      weight_lb: toInt(d.weight_lb),
      seats: toInt(d.seats),
      engine_type: toNull(d.engine_type), // 'gas' | 'electric' | null
    }),
  },
};

function DetailField({ field, value, onChange }) {
  if (field.type === "checkbox") {
    return (
      <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        <span className="memLabel" style={{ margin: 0 }}>
          {field.label}
        </span>
      </label>
    );
  }

  if (field.type === "select") {
    return (
      <div>
        <div className="memLabel">{field.label}</div>
        <select className="memSelect" value={value || ""} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {(field.options || []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // text / number
  return (
    <div>
      <div className="memLabel">{field.label}</div>
      <input
        className="memInput"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        inputMode={field.type === "number" ? "numeric" : undefined}
        placeholder={field.placeholder || ""}
      />
    </div>
  );
}

export default function NewVehicle() {
  const nav = useNavigate();

  const [form, setForm] = useState({
    // base vehicles row
    name: "",
    category: "travel_trailer",
    sub_category: "",

    make: "",
    model: "",
    year: "",
    trim: "",

    status: "available",
    location: "",
    tier_access: ["Explore", "Adventure", "Elite", "Platinum"],

    card_tag: "",
    card_desc: "",
  });

  // category-specific fields
  const [detail, setDetail] = useState({});

  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(null); // null | true | false
  const [err, setErr] = useState(null);

  const cfg = DETAIL_CONFIG[form.category] || null;

  const canSubmit = useMemo(() => {
    return normalize(form.name).length >= 2 && normalize(form.location).length >= 2;
  }, [form.name, form.location]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setDetailField = (k, v) => setDetail((p) => ({ ...p, [k]: v }));

  const toggleTier = (tier) => {
    setForm((p) => {
      const has = p.tier_access.includes(tier);
      const next = has ? p.tier_access.filter((t) => t !== tier) : [...p.tier_access, tier];
      return { ...p, tier_access: next };
    });
  };

  async function onSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setOk(null);
    setErr(null);

    try {
      // 1) insert base row
      const basePayload = {
        name: normalize(form.name),
        category: form.category,
        status: form.status,
        location: toNull(form.location), // location can be null in DB
        tier_access: form.tier_access,

        sub_category: toNull(form.sub_category),
        make: toNull(form.make),
        model: toNull(form.model),
        year: toInt(form.year),
        trim: toNull(form.trim),

        card_tag: toNull(form.card_tag),
        card_desc: toNull(form.card_desc),
      };

      const { data: vRow, error: vErr } = await supabase
        .from("vehicles")
        .insert(basePayload)
        .select("id")
        .single();

      if (vErr) throw vErr;

      const vehicleId = vRow.id;

      // 2) insert detail row
      if (cfg?.table) {
        const detailPayload = cfg.buildPayload(detail);

        // If user left all detail fields blank, skip detail insert (prevents empty rows if you prefer)
        const hasAnyDetailValue = Object.values(detailPayload).some((v) => v !== null && v !== false);
        if (hasAnyDetailValue) {
          const { error: dErr } = await supabase
            .from(cfg.table)
            .insert({ vehicle_id: vehicleId, ...detailPayload });

          if (dErr) throw dErr;
        }
      }

      setOk(true);
      nav(`/admin/vehicles/${vehicleId}`);
    } catch (e2) {
      setOk(false);

      // Helpful message if schema cache is stale
      const msg = e2?.message || "Failed to save vehicle.";
      if (String(msg).toLowerCase().includes("schema cache")) {
        setErr(
          `${msg}\n\nTip: In Supabase SQL editor run:\nselect pg_notify('pgrst','reload schema');`
        );
      } else {
        setErr(msg);
      }
    } finally {
      setSaving(false);
    }
  }

  const categoryLabel =
    CATEGORY_OPTIONS.find((c) => c.value === form.category)?.label || form.category;

  return (
    <div className="page">
      <div className="page__head page__head--row">
        <div>
          <div className="page__back">
            <Link className="link" to="/admin/vehicles">← Vehicles</Link>
          </div>
          <h1 className="page__title">Add Vehicle</h1>
          <div className="page__sub">
            Creates <b>public.vehicles</b> + <b>{cfg?.table || "no detail table"}</b> (category specs).
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {ok === true && <Badge tone="success">Saved</Badge>}
          {ok === false && <Badge tone="danger">Error</Badge>}
        </div>
      </div>

      {err && (
        <div className="adminAuth__error" style={{ marginBottom: 12, whiteSpace: "pre-wrap" }}>
          {err}
        </div>
      )}

      <form onSubmit={onSubmit}>
        <div className="grid grid--2">
          {/* Base */}
          <Card title="Vehicle details" subtitle="Core fields stored in public.vehicles">
            <div className="stack">
              <div>
                <div className="memLabel">Vehicle name</div>
                <input
                  className="memInput"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder='Example: "Jayco Jay Flight 267BHS"'
                  autoFocus
                />
              </div>

              <div className="grid grid--2" style={{ gap: 12 }}>
                <div>
                  <div className="memLabel">Category</div>
                  <select
                    className="memSelect"
                    value={form.category}
                    onChange={(e) => {
                      setField("category", e.target.value);
                      setDetail({});
                    }}
                  >
                    {CATEGORY_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="memLabel">Ops status</div>
                  <select
                    className="memSelect"
                    value={form.status}
                    onChange={(e) => setField("status", e.target.value)}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <div className="memMuted" style={{ marginTop: 6 }}>
                    Use <b>available</b> unless it’s in maintenance/cleaning/transit.
                  </div>
                </div>
              </div>

              <div>
                <div className="memLabel">Sub category (optional)</div>
                <input
                  className="memInput"
                  value={form.sub_category}
                  onChange={(e) => setField("sub_category", e.target.value)}
                  placeholder='Examples: "Fishing", "Pontoon", "Couples", etc.'
                />
                <div className="memMuted" style={{ marginTop: 6 }}>
                  Saved to <b>vehicles.sub_category</b>.
                </div>
              </div>

              <div className="grid grid--2" style={{ gap: 12 }}>
                <div>
                  <div className="memLabel">Make (optional)</div>
                  <input className="memInput" value={form.make} onChange={(e) => setField("make", e.target.value)} />
                </div>
                <div>
                  <div className="memLabel">Model (optional)</div>
                  <input className="memInput" value={form.model} onChange={(e) => setField("model", e.target.value)} />
                </div>
              </div>

              <div className="grid grid--2" style={{ gap: 12 }}>
                <div>
                  <div className="memLabel">Year (optional)</div>
                  <input
                    className="memInput"
                    value={form.year}
                    onChange={(e) => setField("year", e.target.value)}
                    placeholder="2024"
                    inputMode="numeric"
                  />
                </div>
                <div>
                  <div className="memLabel">Trim (optional)</div>
                  <input className="memInput" value={form.trim} onChange={(e) => setField("trim", e.target.value)} />
                </div>
              </div>

              <div>
                <div className="memLabel">Location</div>
                <input
                  className="memInput"
                  value={form.location}
                  onChange={(e) => setField("location", e.target.value)}
                  placeholder='Example: "Woodstock Warehouse"'
                />
              </div>

              <div className="grid grid--2" style={{ gap: 12 }}>
                <div>
                  <div className="memLabel">Card tag (optional)</div>
                  <input className="memInput" value={form.card_tag} onChange={(e) => setField("card_tag", e.target.value)} />
                </div>
                <div>
                  <div className="memLabel">Card description (optional)</div>
                  <input className="memInput" value={form.card_desc} onChange={(e) => setField("card_desc", e.target.value)} />
                </div>
              </div>
            </div>
          </Card>

          {/* Tier Access */}
          <Card title="Tier access" subtitle="Who can book this vehicle?">
            <div className="stack">
              <div className="memMuted">Select at least one tier.</div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 6 }}>
                {TIER_OPTIONS.map((t) => {
                  const active = form.tier_access.includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      className={`btn ${active ? "btn--primary" : "btn--ghost"}`}
                      onClick={() => toggleTier(t)}
                    >
                      {active ? "✓ " : ""}
                      {t}
                    </button>
                  );
                })}
              </div>

              <div style={{ marginTop: 10 }}>
                <div className="memLabel">Preview</div>
                <div className="stack" style={{ gap: 8 }}>
                  <div className="muted">
                    <b>{form.name || "Vehicle name"}</b> • {categoryLabel} • {form.location || "Location"}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Badge tone="info">{form.status}</Badge>
                    {form.sub_category ? <Badge tone="neutral">{form.sub_category}</Badge> : null}
                    {form.tier_access.map((t) => (
                      <Badge key={t} tone="neutral">
                        {t}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Category Details */}
          <Card
            title={`${categoryLabel} specs`}
            subtitle={cfg ? `Saved to ${cfg.table}` : "No detail table configured for this category."}
          >
            {!cfg ? (
              <div className="memMuted">
                No detail table configured. The vehicle will still save to <b>public.vehicles</b>.
              </div>
            ) : (
              <div className="stack">
                <div className="memMuted">
                  These fields are category-specific and saved to <b>{cfg.table}</b>.
                </div>

                <div className="grid grid--2" style={{ gap: 12 }}>
                  {cfg.fields.map((f) => (
                    <DetailField
                      key={f.key}
                      field={f}
                      value={detail[f.key]}
                      onChange={(val) => setDetailField(f.key, val)}
                    />
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Save */}
          <Card title="Save" subtitle="Write to vehicles + details table">
            <div className="stack">
              <div className="memMuted">
                This will create a base vehicle row and then a {cfg?.table ? cfg.table : "details"} row.
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button className="btn btn--primary" type="submit" disabled={!canSubmit || saving}>
                  {saving ? "Saving…" : "Save Vehicle"}
                </button>
                <Link className="btn btn--ghost" to="/admin/vehicles">
                  Cancel
                </Link>
              </div>

              {!canSubmit && (
                <div className="memMuted">
                  Add at least a <b>name</b> and <b>location</b> to save.
                </div>
              )}
            </div>
          </Card>
        </div>
      </form>
    </div>
  );
}